import type { UIMessage } from '@ai-sdk/react'
import {
  Conversation,
  ConversationContent,
  ConversationScrollButton,
} from '@neo/blocks/ai-elements/conversation'
import { MessageAction } from '@neo/blocks/ai-elements/message'
import {
  PromptInput,
  PromptInputFooter,
  PromptInputSubmit,
  PromptInputTextarea,
} from '@neo/blocks/ai-elements/prompt-input'
import { Button } from '@neo/index'
import { Tooltip } from '@neo/primitives/tooltip'
import { useKeyboardShortcuts } from '@shared/hooks/use-keyboard-shortcuts'
import { checkIfBrowserSupported, takeScreenshot } from '@xata.io/screenshot'
import type { FileUIPart } from 'ai'
import type { AxiosError } from 'axios'
import {
  Camera,
  ChevronRight,
  FileText,
  Info,
  MessageSquarePlus,
  Paperclip,
  User,
  X,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { REGIONS } from 'v2/configs/constants'
import { queryClient } from 'v2/configs/rq-setup'
import type { RAccountDetails } from 'v2/pages/billing/types'
import { getAccountId } from 'v2/pages/billing/utils'
import { useFeatureFlags } from 'v2/shared/hooks/use-feature-flags'
import { useUserPlan } from 'v2/shared/hooks/use-plan'
import { createGlobalHeaders, resolveBaseUrl } from 'v2/shared/services/create-service'
import { billingQueryKeys, getSettingsQueryKeys } from 'v2/shared/utils/query-key-management'
import { useAppStore } from 'v2/state/app'
import type { RUserProfile } from 'v2/types/api/settings'
import { getLocalAuth } from 'v2/utils'
import { AnalyticsEvents, track } from 'v2/utils/analytics'
import { errorToast } from 'v2/utils/toasts'
import { AssistantMessage, AssistantMessageLoading } from './assistant-message'
import { AssistantBanner } from './components/assistant-banner'
import { AssistantEmptyState } from './components/empty-state'
import { FeedbackDialog } from './components/feedback-dialog'
import { TicketDialog, type TicketFields } from './components/ticket-dialog'
import { raiseTicket } from './ticket-service'
import { type BuddyChatBody, useBuddyChat } from './use-buddy-chat'
import { useFeedbackPrompt } from './use-feedback-prompt'
import {
  completeSlashCommand,
  getBuddySuggestions,
  hasCompletedEscalation,
  is429Error,
  MAX_LENGTH,
  sanitizeInput,
} from './utils'

export const ASSISTANT_PANEL_WIDTH = 420
const MAX_ATTACHMENTS = 3
const MAX_FILE_SIZE = 5 * 1024 * 1024
// Cap Buddy's answer sent to PostHog (response_length keeps the true length).
const MAX_ANALYTICS_RESPONSE_LEN = 10000
// Keep in sync with SUPPORTED_BUDDY_ATTACHMENT_TYPES in aiassist models/buddy.py —
// the backend re-validates and 422s on anything outside this set.
const ACCEPTED_FILE_TYPES = {
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/webp': ['.webp'],
  'application/pdf': ['.pdf'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
}
const INVALID_TYPE_MESSAGE = 'Only PNG, JPEG, WebP, PDF, DOCX and XLSX files are supported.'

export type BuddyEngine = 'aiassist' | 'flow'

interface AttachmentPolicy {
  accept: Record<string, string[]>
  maxBytes: number
  invalidTypeMessage: string
}

const { 'image/webp': _webp, ...FLOW_ACCEPTED_FILE_TYPES } = ACCEPTED_FILE_TYPES

const ATTACHMENT_POLICIES: Record<BuddyEngine, AttachmentPolicy> = {
  aiassist: {
    accept: ACCEPTED_FILE_TYPES,
    maxBytes: MAX_FILE_SIZE,
    invalidTypeMessage: INVALID_TYPE_MESSAGE,
  },
  // Flow attachments go through the chat SDK's uploader: no WebP, and a 5,000,000-byte cap.
  flow: {
    accept: FLOW_ACCEPTED_FILE_TYPES,
    maxBytes: 5_000_000,
    invalidTypeMessage: 'Only PNG, JPEG, PDF, DOCX and XLSX files are supported.',
  },
}

type BuddyTriggerType = 'typed' | 'suggestion' | 'escalation'

// Matches the `/ticket` slash command (optionally followed by a description),
// e.g. "/ticket", "/ticket: my issue", "/ticket my number isn't working".
const TICKET_COMMAND_RE = /^\/ticket\b[:\s]*/i

const TICKET_SUCCESS_TEXT =
  'Your support ticket has been raised — our team will get back to you by email shortly. Feel free to keep chatting with Buddy in the meantime.'

const newMsgId = (): string =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `id-${Date.now()}-${Math.random().toString(36).slice(2)}`

// Shared so the success thread record and the failure composer seed cannot drift.
const joinTicketText = (subject: string, description: string): string =>
  subject.trim() ? `${subject.trim()}\n\n${description.trim()}` : description.trim()

const fileToDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = reject
    reader.readAsDataURL(file)
  })

// Shared by every chat turn and the feedback vote, so both carry the same user context.
const buildChatBody = (): BuddyChatBody => {
  const auth = getLocalAuth()
  const profileData = queryClient.getQueryData<RUserProfile>(getSettingsQueryKeys().profile)
  const accountId = getAccountId()
  const accountDetails = queryClient.getQueryData<RAccountDetails>(
    billingQueryKeys().accountDetails(accountId),
  )
  const account = accountDetails?.account
  const conversionRate = account?.conversion_rate || 1
  const balanceData = queryClient.getQueryData<{ amount?: number }>(
    billingQueryKeys().balance(accountId, conversionRate),
  )

  return {
    userContext: {
      email: auth?.email,
      name: auth?.name,
      roles: auth?.roles,
      plan: profileData?.plan,
      balance: balanceData?.amount,
      currency: accountDetails?.account?.currency,
      conversionRate: accountDetails?.account?.conversion_rate,
      region: auth?.region,
      countryId: account?.country_id,
      registeredIp: account?.registered_ip,
      signupDate: account?.created,
    },
    pageUrl: window.location.href,
  }
}

interface AssistantPanelProps {
  engine?: BuddyEngine
  useChat?: typeof useBuddyChat
  // Keeps a queued prompt unsent while feature flags load, so the engine they pick sends it.
  holdPendingPrompt?: boolean
}

export const AssistantPanel = ({
  engine = 'aiassist',
  useChat = useBuddyChat,
  holdPendingPrompt = false,
}: AssistantPanelProps) => {
  const attachmentPolicy = ATTACHMENT_POLICIES[engine]
  const isOpen = useAppStore((s) => s.isAssistantOpen)
  const setIsOpen = useAppStore((s) => s.setIsAssistantOpen)
  const pendingPrompt = useAppStore((s) => s.pendingAssistantPrompt)
  const setPendingPrompt = useAppStore((s) => s.setPendingAssistantPrompt)
  const { isBuddyDirectTicketEnabled } = useFeatureFlags()
  const { isEnterprisePlan } = useUserPlan()
  // Enterprise accounts always get /ticket; everyone else is PostHog-gated.
  // isEnterprisePlan is the plan-derived tier (not the grandfathered
  // isEnterpriseLocked gate) and is false until the profile resolves, so a
  // non-flagged enterprise user sees the command appear on plan load.
  const isTicketCommandEnabled = isEnterprisePlan || isBuddyDirectTicketEnabled

  useKeyboardShortcuts([
    {
      shortcut: { key: 'i', meta: true },
      method: () => {
        setIsOpen(!isOpen)
      },
    },
  ])

  const inputRef = useRef<HTMLTextAreaElement>(null)
  // Stores last sent text so it can be restored to the input on 429 rate limit errors
  const lastSentTextRef = useRef<string>('')
  const cooldownTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Pending buddy_message_sent metadata. Flushed to PostHog only once the
  // server accepts the message (status -> 'streaming'); cleared on error so
  // rate-limited or failed sends don't inflate the sent metric.
  const pendingBuddyTrackRef = useRef<{
    message: string
    messageLength: number
    hasAttachment: boolean
    triggerType: BuddyTriggerType
  } | null>(null)
  const [charCount, setCharCount] = useState(0)
  const [pendingAttachments, setPendingAttachments] = useState<FileUIPart[]>([])
  // Prevents rapid-fire submissions by disabling send for 1s after each message
  const [isCooldown, setIsCooldown] = useState(false)
  // `/ticket` direct-escalation flow: form visibility, the description seeded from
  // the command line, in-flight state, and the slash hint shown while typing "/".
  const [isTicketOpen, setIsTicketOpen] = useState(false)
  const [ticketDraft, setTicketDraft] = useState('')
  const [isSubmittingTicket, setIsSubmittingTicket] = useState(false)
  const [slashHintVisible, setSlashHintVisible] = useState(false)
  // The flow engine cannot see the escalation tool run yet, so the banner treats the settled
  // reply to its own "Contact" message as the escalation.
  const [flowEscalationSettled, setFlowEscalationSettled] = useState(false)

  useEffect(
    () => () => {
      if (cooldownTimerRef.current) clearTimeout(cooldownTimerRef.current)
    },
    [],
  )

  const startCooldown = useCallback(() => {
    setIsCooldown(true)
    if (cooldownTimerRef.current) clearTimeout(cooldownTimerRef.current)
    cooldownTimerRef.current = setTimeout(() => setIsCooldown(false), 1000)
  }, [])

  const { messages, sendMessage, setMessages, resetConversation, status, error, clearError, stop } =
    useChat({
      endpoint: `${resolveBaseUrl().replace(/\/$/, '')}/v1/aiassist/buddy/chat`,
      buildHeaders: () => createGlobalHeaders(),
      buildBody: buildChatBody,
      // Scopes the conversation id to the org, so a logout or org switch in the same
      // tab cannot inherit the previous tenant's id (sessionStorage survives both).
      getSessionOwner: () => String(getAccountId() || ''),
      onFinish: (summary) => {
        feedback.schedule(summary)
        const pending = pendingBuddyTrackRef.current
        if (!pending) return
        track(AnalyticsEvents.BUDDY_MESSAGE_SENT, {
          message_text: pending.message,
          message_length: pending.messageLength,
          has_attachment: pending.hasAttachment,
          trigger_type: pending.triggerType,
          response_text: summary.responseText.slice(0, MAX_ANALYTICS_RESPONSE_LEN),
          response_length: summary.responseText.length,
          tools_used: summary.toolsUsed,
          response_had_error: summary.hadError,
          session_id: summary.sessionId,
          ...(engine === 'flow' ? { engine } : {}),
        })
        if (engine === 'flow' && pending.triggerType === 'escalation') {
          setFlowEscalationSettled(true)
        }
        pendingBuddyTrackRef.current = null
      },
      onError: () => {
        pendingBuddyTrackRef.current = null
      },
    })

  const isLoading = status === 'streaming' || status === 'submitted'
  const hasMessages = messages.length > 0
  const rateLimitError = error && is429Error(error)
  const isComposerEmpty = charCount === 0 && pendingAttachments.length === 0

  const feedback = useFeedbackPrompt({
    isPanelOpen: isOpen,
    isChatBusy: isLoading,
    isComposerEmpty,
    hasConversation: hasMessages,
    isTicketFlowActive: isTicketOpen || isSubmittingTicket,
  })

  const userMessageCount = messages.filter((m) => m.role === 'user').length
  const escalationCompleted = hasCompletedEscalation(messages) || flowEscalationSettled
  const escalationShown = useRef(false)
  const shouldShowEscalation = !escalationCompleted && userMessageCount > 5
  if (shouldShowEscalation) escalationShown.current = true
  if (!hasMessages) escalationShown.current = false
  const escalationVisible = escalationShown.current && !escalationCompleted

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const remaining = MAX_ATTACHMENTS - pendingAttachments.length
      const filesToProcess = acceptedFiles.slice(0, remaining)
      const parts: FileUIPart[] = await Promise.all(
        filesToProcess.map(async (file) => ({
          type: 'file' as const,
          mediaType: file.type,
          filename: file.name,
          url: await fileToDataUrl(file),
        })),
      )
      setPendingAttachments((prev) => [...prev, ...parts].slice(0, MAX_ATTACHMENTS))
    },
    [pendingAttachments.length],
  )

  const {
    getRootProps,
    getInputProps,
    open: openFilePicker,
  } = useDropzone({
    onDrop,
    accept: attachmentPolicy.accept,
    maxSize: attachmentPolicy.maxBytes,
    multiple: true,
    noClick: true,
    noKeyboard: true,
    disabled: pendingAttachments.length >= MAX_ATTACHMENTS,
    onDropRejected: (rejections) => {
      const firstError = rejections[0]?.errors[0]
      if (firstError?.code === 'file-too-large') {
        errorToast({ title: 'File too large', description: 'Max file size is 5 MB.' })
      } else if (firstError?.code === 'file-invalid-type') {
        errorToast({ title: 'Invalid file type', description: attachmentPolicy.invalidTypeMessage })
      } else {
        errorToast({ title: 'Upload failed', description: firstError?.message ?? 'Unknown error' })
      }
    },
  })

  const removeAttachment = useCallback((index: number) => {
    setPendingAttachments((prev) => prev.filter((_, i) => i !== index))
  }, [])

  const handlePaste = useCallback(
    async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      const items = e.clipboardData?.items
      if (!items) return
      const hasFileItem = Array.from(items).some((item) => item.kind === 'file')
      if (!hasFileItem) return
      if (pendingAttachments.length >= MAX_ATTACHMENTS) {
        e.preventDefault()
        errorToast({
          title: 'Attachment limit reached',
          description: `You can attach up to ${MAX_ATTACHMENTS} files.`,
        })
        return
      }
      const acceptedTypes = Object.keys(attachmentPolicy.accept)
      const pastedFiles: File[] = []
      let hasOversized = false
      let hasInvalidType = false
      for (const item of items) {
        if (item.kind !== 'file') continue
        const file = item.getAsFile()
        if (!file) continue
        if (!acceptedTypes.includes(file.type)) {
          hasInvalidType = true
          continue
        }
        if (file.size > attachmentPolicy.maxBytes) {
          hasOversized = true
          continue
        }
        pastedFiles.push(file)
      }
      if (pastedFiles.length === 0) {
        if (hasOversized) {
          e.preventDefault()
          errorToast({ title: 'File too large', description: 'Max file size is 5 MB.' })
        } else if (hasInvalidType) {
          e.preventDefault()
          errorToast({
            title: 'Invalid file type',
            description: attachmentPolicy.invalidTypeMessage,
          })
        }
        return
      }
      e.preventDefault()
      try {
        await onDrop(pastedFiles)
      } catch {
        errorToast({ title: 'Upload failed', description: 'Could not process pasted file.' })
      }
    },
    [onDrop, pendingAttachments.length, attachmentPolicy],
  )

  const isBrowserSupported = checkIfBrowserSupported()

  const handleCaptureScreenshot = useCallback(async () => {
    if (pendingAttachments.length >= MAX_ATTACHMENTS) return
    try {
      const dataUrl = await takeScreenshot({ type: 'image/png', quality: 0.9 })
      const part: FileUIPart = {
        type: 'file',
        mediaType: 'image/png',
        filename: 'screenshot.png',
        url: dataUrl,
      }
      setPendingAttachments((prev) => [...prev, part].slice(0, MAX_ATTACHMENTS))
    } catch {
      errorToast({ title: 'Screenshot failed', description: 'Could not capture screenshot.' })
    }
  }, [pendingAttachments.length])

  // Handle errors from useChat: show toast for non-429, remove failed message and restore input for 429
  useEffect(() => {
    if (!error) return
    if (is429Error(error)) {
      setMessages((prev) => {
        const last = prev[prev.length - 1]
        return last?.role === 'user' ? prev.slice(0, -1) : prev
      })
      if (inputRef.current && lastSentTextRef.current) {
        inputRef.current.value = lastSentTextRef.current
        setCharCount(lastSentTextRef.current.length)
      }
    } else {
      errorToast({
        title: 'Something went wrong',
        description: 'Failed to get a response. Please try again.',
      })
    }
  }, [error, setMessages])

  useEffect(() => {
    if (!isOpen) return
    const timer = setTimeout(() => inputRef.current?.focus(), 220)
    return () => clearTimeout(timer)
  }, [isOpen])

  // Auto-send a preloaded prompt when one is queued (e.g. from a "Contact support" CTA).
  // Waits for the panel to open and the chat to be idle so the message isn't dropped.
  useEffect(() => {
    if (!isOpen || !pendingPrompt || isLoading || isCooldown || holdPendingPrompt) return
    const sanitized = sanitizeInput(pendingPrompt)
    if (!sanitized) {
      setPendingPrompt(null)
      return
    }
    lastSentTextRef.current = sanitized
    sendMessage({ text: sanitized })
    pendingBuddyTrackRef.current = {
      message: sanitized,
      messageLength: sanitized.length,
      hasAttachment: false,
      triggerType: 'suggestion',
    }
    startCooldown()
    setPendingPrompt(null)
  }, [
    isOpen,
    pendingPrompt,
    isLoading,
    isCooldown,
    holdPendingPrompt,
    sendMessage,
    startCooldown,
    setPendingPrompt,
  ])

  // Uncontrolled textarea: value and counter are two writes and must stay together.
  const setComposerText = useCallback((text: string) => {
    // Clamped because assigning .value programmatically bypasses the textarea's
    // maxLength, and the submit guard only rejects empty text, not overlong text.
    const clamped = text.slice(0, MAX_LENGTH)
    if (inputRef.current) inputRef.current.value = clamped
    setCharCount(clamped.length)
  }, [])

  // Opens the ticket popup, seeding the description with any text the user typed
  // after `/ticket`. Clears the composer + pending attachments so the dialog owns the
  // interaction.
  const openTicket = useCallback(
    (draft: string) => {
      setTicketDraft(draft)
      setIsTicketOpen(true)
      setSlashHintVisible(false)
      setPendingAttachments([])
      setComposerText('')
    },
    [setComposerText],
  )

  const handleFormSubmit = useCallback(
    ({ text }: { text: string }) => {
      const sanitized = sanitizeInput(text)
      const hasAttachments = pendingAttachments.length > 0
      if ((!sanitized && !hasAttachments) || isLoading || isCooldown) return
      // `/ticket` (flagged users only) bypasses the AI: open the direct ticket
      // form instead of sending the message to Buddy.
      if (isTicketCommandEnabled && TICKET_COMMAND_RE.test(sanitized)) {
        openTicket(sanitized.replace(TICKET_COMMAND_RE, '').trim())
        return
      }
      if (rateLimitError) clearError()
      lastSentTextRef.current = sanitized
      if (hasAttachments) {
        sendMessage({ text: sanitized || '', files: pendingAttachments })
      } else {
        sendMessage({ text: sanitized })
      }
      pendingBuddyTrackRef.current = {
        message: sanitized,
        messageLength: sanitized.length,
        hasAttachment: hasAttachments,
        triggerType: 'typed',
      }
      setPendingAttachments([])
      setCharCount(0)
      startCooldown()
    },
    [
      isLoading,
      isCooldown,
      sendMessage,
      rateLimitError,
      clearError,
      startCooldown,
      pendingAttachments,
      isTicketCommandEnabled,
      openTicket,
    ],
  )

  const handleReset = useCallback(() => {
    // Abort any in-flight stream and drop pending tracking so a reset can't
    // flush a buddy_message_sent with an empty/mismatched response.
    stop()
    pendingBuddyTrackRef.current = null
    // Clears the thread AND mints a fresh conversation id. Distinct from the 429
    // cleanup below, which empties the list without starting a new conversation.
    resetConversation()
    setPendingAttachments([])
    setFlowEscalationSettled(false)
  }, [stop, resetConversation])

  const handleSlashCommandKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (!isTicketCommandEnabled || e.key !== 'Tab' || e.shiftKey) return

      const el = e.currentTarget
      const completed = completeSlashCommand(el.value)
      if (!completed) return

      e.preventDefault()
      el.value = completed
      el.dispatchEvent(new Event('input', { bubbles: true }))
      setCharCount(completed.length)
      setSlashHintVisible(completed.trimStart().startsWith('/'))
      el.setSelectionRange(completed.length, completed.length)
    },
    [isTicketCommandEnabled],
  )

  const handleEscalate = useCallback(() => {
    if (isTicketCommandEnabled) {
      openTicket('')
      return
    }
    if (isCooldown || isLoading) return
    const text = 'I would like to escalate to support'
    sendMessage({ text })
    pendingBuddyTrackRef.current = {
      message: text,
      messageLength: text.length,
      hasAttachment: false,
      triggerType: 'escalation',
    }
    startCooldown()
  }, [sendMessage, startCooldown, isCooldown, isLoading, isTicketCommandEnabled, openTicket])

  const handleTicketSubmit = useCallback(
    async ({ subject, description, ccEmails }: TicketFields) => {
      setIsSubmittingTicket(true)
      try {
        const ccList = ccEmails
          .split(',')
          .map((e) => e.trim())
          .filter(Boolean)
        await raiseTicket({ subject, description, ccEmails: ccList })
        // Mirror the submission into the thread so there's a persistent record:
        // the user's request followed by Buddy's confirmation.
        const confirmation: UIMessage[] = [
          {
            id: newMsgId(),
            role: 'user',
            parts: [{ type: 'text', text: joinTicketText(subject, description) }],
          },
          {
            id: newMsgId(),
            role: 'assistant',
            parts: [{ type: 'text', text: TICKET_SUCCESS_TEXT }],
          },
        ]
        setMessages((prev) => [...prev, ...confirmation])
        setIsTicketOpen(false)
        setTicketDraft('')
      } catch (error) {
        // 403 is in the interceptor's ignoredStatusCodes, so nothing logs this but us.
        const status = (error as AxiosError | undefined)?.response?.status
        track(AnalyticsEvents.BUDDY_TICKET_FAILED, { status_code: status ?? null })
        // Dialog is modal, so hand the text back to the composer to make the fallback
        // usable. A ticket can hold more than the composer accepts, so when it does not
        // fit, keep the dialog open instead of truncating the user's words away.
        const restored = joinTicketText(subject, description)
        const fitsComposer = restored.length <= MAX_LENGTH
        if (fitsComposer) {
          setIsTicketOpen(false)
          setTicketDraft('')
          setComposerText(restored)
        }
        errorToast({
          title: 'Could not raise ticket',
          description: fitsComposer
            ? 'Please try again, or ask Buddy in the chat below and it will raise this for you.'
            : 'Please try again.',
        })
      } finally {
        setIsSubmittingTicket(false)
      }
    },
    [setMessages, setComposerText],
  )

  const handleSuggestionClick = useCallback(
    (suggestion: string) => {
      const sanitized = sanitizeInput(suggestion)
      // isLoading guard prevents overwriting pendingBuddyTrackRef mid-stream.
      if (!sanitized || isCooldown || isLoading) return
      sendMessage({ text: sanitized })
      pendingBuddyTrackRef.current = {
        message: sanitized,
        messageLength: sanitized.length,
        hasAttachment: false,
        triggerType: 'suggestion',
      }
      startCooldown()
    },
    [sendMessage, isCooldown, isLoading, startCooldown],
  )

  // Region is fixed for the session, so this only needs to run once.
  const suggestions = useMemo(() => getBuddySuggestions(getLocalAuth()?.region === REGIONS.IN), [])

  return (
    <div
      className={`flex-shrink-0 h-screen transition-[width,visibility] duration-200 ease-linear overflow-hidden ${
        isOpen ? 'w-[420px] border-l visible' : 'w-0 invisible'
      }`}
      data-testid="assistant-panel"
    >
      <div className="flex flex-col w-[420px] h-full">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 h-14">
          <span className="text-p-600">Ask Buddy</span>
          <div className="flex items-center gap-1">
            {hasMessages && (
              <MessageAction
                size="iconSm"
                tooltip="Start new conversation"
                onClick={handleReset}
                data-testid="assistant-reset-btn"
              >
                <MessageSquarePlus className="w-4 h-4" />
              </MessageAction>
            )}
            <MessageAction
              size="iconSm"
              tooltip="Close"
              onClick={() => setIsOpen(false)}
              data-testid="assistant-close-btn"
            >
              <ChevronRight className="w-4 h-4" />
            </MessageAction>
          </div>
        </div>

        {/* Messages */}
        <Conversation>
          <ConversationContent>
            {!hasMessages && (
              <AssistantEmptyState
                suggestions={suggestions}
                onSuggestionClick={handleSuggestionClick}
              />
            )}

            {messages.map((message, index) => (
              <AssistantMessage
                key={message.id}
                message={message}
                isActive={isLoading && index === messages.length - 1}
              />
            ))}

            {isLoading && messages.length > 0 && messages[messages.length - 1].role === 'user' && (
              <AssistantMessageLoading />
            )}
          </ConversationContent>
          <ConversationScrollButton />
        </Conversation>

        {/* Input */}
        <div className="flex flex-col mt-auto relative overflow-hidden flex-shrink-0 p-4">
          <AssistantBanner
            visible={!!rateLimitError}
            className="border-status-danger/25 bg-status-danger/[0.08]"
          >
            <Info className="w-3 h-3 text-status-danger flex-shrink-0" />
            <span className="text-xxs-400 text-status-danger">
              Rate limit reached. Please wait a moment before sending another message.
            </span>
          </AssistantBanner>
          <AssistantBanner visible={escalationVisible}>
            <Info className="w-3 h-3 text-muted-foreground flex-shrink-0" />
            <span className="text-xxs-400 text-muted-foreground flex-1">
              Having trouble? Connect with our support team.
            </span>
            <div className="flex items-center gap-1 flex-shrink-0">
              <Button
                size="xs"
                variant="outline"
                onClick={handleEscalate}
                data-testid="assistant-escalation-confirm-btn"
              >
                Contact
              </Button>
            </div>
          </AssistantBanner>
          {slashHintVisible && (
            <button
              type="button"
              onClick={() => openTicket('')}
              className="flex items-center gap-1.5 mb-2 text-xxs-400 text-muted-foreground hover:text-foreground transition-colors"
              data-testid="assistant-ticket-hint-btn"
            >
              <span className="rounded bg-muted px-1 py-0.5 text-foreground">/ticket</span>
              Raise a support ticket directly
            </button>
          )}
          <div {...getRootProps()} className="relative">
            <input {...getInputProps()} data-testid="assistant-attachment-dropzone-input" />
            <PromptInput onSubmit={handleFormSubmit} className="bg-background z-10 relative">
              <PromptInputTextarea
                ref={inputRef}
                maxLength={MAX_LENGTH}
                onKeyDown={handleSlashCommandKeyDown}
                onInput={(e: React.FormEvent<HTMLTextAreaElement>) => {
                  const { value } = e.currentTarget
                  setCharCount(value.length)
                  setSlashHintVisible(isTicketCommandEnabled && value.trimStart().startsWith('/'))
                }}
                onPaste={handlePaste}
                data-testid="assistant-input"
              />
              {pendingAttachments.length > 0 && (
                <div
                  className="flex items-center gap-2 px-3 py-2 overflow-x-auto"
                  data-testid="assistant-attachment-preview-strip"
                >
                  {pendingAttachments.map((attachment, index) => (
                    <div
                      key={attachment.filename ?? index}
                      className="relative flex-shrink-0 group"
                    >
                      {attachment.mediaType.startsWith('image/') ? (
                        <img
                          src={attachment.url}
                          alt={attachment.filename || 'Preview'}
                          className="h-14 w-14 rounded-md object-cover"
                        />
                      ) : (
                        <div className="flex h-14 w-32 items-center gap-1.5 rounded-md border border-st1 px-2">
                          <FileText className="w-4 h-4 flex-shrink-0 text-muted-foreground" />
                          <span className="truncate text-xxs-400" title={attachment.filename}>
                            {attachment.filename || 'File'}
                          </span>
                        </div>
                      )}
                      <Button
                        type="button"
                        variant="ghost"
                        size="iconSm"
                        aria-label="Remove attachment"
                        className="absolute -top-1.5 -right-1.5 h-5 w-5 bg-foreground text-background hover:bg-foreground/90 hover:text-background"
                        onClick={() => removeAttachment(index)}
                        data-testid={`assistant-attachment-remove-${index}-btn`}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              <PromptInputFooter>
                <div className="flex items-center gap-2">
                  <Tooltip content="Upload file">
                    <Button
                      type="button"
                      variant="ghost"
                      size="iconSm"
                      aria-label="Upload file"
                      onClick={openFilePicker}
                      disabled={pendingAttachments.length >= MAX_ATTACHMENTS}
                      data-testid="assistant-attachment-upload-btn"
                    >
                      <Paperclip className="w-3.5 h-3.5" />
                    </Button>
                  </Tooltip>
                  {isBrowserSupported && (
                    <Tooltip content="Capture screenshot">
                      <Button
                        type="button"
                        variant="ghost"
                        size="iconSm"
                        aria-label="Capture screenshot"
                        onClick={handleCaptureScreenshot}
                        disabled={pendingAttachments.length >= MAX_ATTACHMENTS}
                        data-testid="assistant-screenshot-btn"
                      >
                        <Camera className="w-4 h-4" />
                      </Button>
                    </Tooltip>
                  )}
                  {isTicketCommandEnabled && (
                    <Tooltip content="Raise a support ticket">
                      <Button
                        type="button"
                        variant="ghost"
                        size="iconSm"
                        aria-label="Raise a support ticket"
                        onClick={() => openTicket('')}
                        data-testid="assistant-ticket-icon-btn"
                      >
                        <User className="w-4 h-4" />
                      </Button>
                    </Tooltip>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-s-400 text-muted-foreground pr-1">
                    {charCount} / {MAX_LENGTH}
                  </span>
                  <PromptInputSubmit
                    status={status}
                    disabled={(isComposerEmpty && !isLoading) || isCooldown}
                    className="h-7 w-7"
                    data-testid="assistant-send-btn"
                  />
                </div>
              </PromptInputFooter>
            </PromptInput>
          </div>
        </div>
        <TicketDialog
          open={isTicketOpen}
          onOpenChange={(o) => {
            if (isSubmittingTicket) return
            setIsTicketOpen(o)
            if (!o) setTicketDraft('')
          }}
          initialDescription={ticketDraft}
          isSubmitting={isSubmittingTicket}
          onSubmit={handleTicketSubmit}
        />
        {feedback.prompt && (
          <FeedbackDialog
            open={feedback.isOpen}
            prompt={feedback.prompt}
            getUserContext={() => buildChatBody().userContext}
            onClose={feedback.close}
          />
        )}
      </div>
    </div>
  )
}
